<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Select, Textarea } from '@drama/ui'
import FormField from '../NovelFormField.vue'
import NovelIssueNote from '../NovelIssueNote.vue'
import NovelOpeningAssistant from '../NovelOpeningAssistant.vue'
import { useSectionModel } from './section-model.js'
import './novel-sections.css'

const props = defineProps({
  modelValue: { type: Object, required: true },
  issues: { type: Array, default: () => [] },
  upload: { type: Function, default: null },
  assist_opening: { type: Function, default: null },
  assistant_disabled: { type: Boolean, default: false },
  guided: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'blocked'])
const { t } = useI18n()
const { bind, invalid, notes, removeEntity, write, error } = useSectionModel(props, emit)
const segments = computed(() => props.modelValue.opening?.segments ?? [])
const speakerOptions = computed(() => [
  { value: 'narrator', label: t('novelStudio.fields.narrator') },
  ...(props.modelValue.characters ?? [])
    .filter(person => person.id !== props.modelValue.player_id)
    .map(person => ({ value: person.id, label: props.guided ? person.name || person.id : `${person.name || person.id} · ${person.id}` })),
])

/** 不落在某一段里的错误（段落列表本身、段数等），显示在列表上方；段内错误显示在各段卡片里。 */
const sectionNotes = computed(() => props.issues
  .filter(issue => !/^opening\.segments\.\d+/.test(issue.path))
  .map(issue => ({ key: issue.key, text: issue.note })))

/** 旁白只能用 narration；人物只能用 dialogue 或 action（与 Demo 编辑器一致）。 */
function channelOptions(speaker_id) {
  const channels = speaker_id === 'narrator' ? ['narration'] : ['dialogue', 'action']
  return channels.map(channel => ({ value: channel, label: t(`novelStudio.channels.${channel}`) }))
}

function setSpeaker(index, speaker_id) {
  const segment = { ...segments.value[index], speaker_id }
  if (speaker_id === 'narrator') segment.channel = 'narration'
  else if (segment.channel === 'narration') segment.channel = 'dialogue'
  write(['opening', 'segments', index], segment)
}

function addSegment() {
  write(['opening', 'segments'], [...segments.value, { speaker_id: 'narrator', channel: 'narration', text: '' }])
}
</script>

<template>
  <section
    class="novel-entities"
    data-testid="novel-opening-section"
  >
    <NovelOpeningAssistant
      v-if="assist_opening"
      :model-value="modelValue"
      :assist_opening="assist_opening"
      :disabled="assistant_disabled"
      @update:model-value="emit('update:modelValue', $event)"
    />
    <header class="novel-entities__header">
      <p>{{ t('novelStudio.fields.count', { count: segments.length }) }}</p>
      <Button
        variant="secondary"
        size="sm"
        data-testid="novel-add-segment"
        @click="addSegment"
      >
        {{ t('novelStudio.fields.addSegment') }}
      </Button>
    </header>
    <p class="novel-entities__hint">
      {{ t('novelStudio.fields.referenceHelp') }}
    </p>
    <NovelIssueNote :notes="sectionNotes" />
    <p
      v-if="!segments.length"
      class="novel-entities__empty"
    >
      {{ t('novelStudio.empty.opening') }}
    </p>
    <article
      v-for="(segment, index) in segments"
      :key="index"
      class="novel-entity"
      :class="{ 'has-error': invalid(['opening', 'segments', index]) }"
      :data-path="`opening.segments.${index}`"
      tabindex="-1"
      :data-testid="`novel-opening-${index}`"
    >
      <header class="novel-entity__header">
        <div class="novel-entity__title">
          <h3 v-if="guided">
            {{ t('novelStudio.fields.text') }} {{ index + 1 }}
          </h3>
          <code v-else>opening-{{ index }}</code>
        </div>
        <Button
          variant="secondary"
          size="sm"
          :aria-label="t('novelStudio.fields.removeNamed', { name: `opening-${index}` })"
          :data-testid="`novel-opening-${index}-remove`"
          @click="removeEntity('opening', index, `opening-${index}`)"
        >
          {{ t('novelStudio.fields.remove') }}
        </Button>
      </header>
      <NovelIssueNote :notes="notes(['opening', 'segments', index])" />
      <div class="novel-entity__grid">
        <FormField
          :error="error(['opening', 'segments', index, 'speaker_id'])"
          required
          :label="t('novelStudio.fields.speaker')"
        >
          <Select
            :model-value="segment.speaker_id"
            :options="speakerOptions"
            :aria-label="t('novelStudio.fields.speaker')"
            :invalid="invalid(['opening', 'segments', index, 'speaker_id'])"
            :data-path="`opening.segments.${index}.speaker_id`"
            block
            @update:model-value="setSpeaker(index, $event)"
          />
        </FormField>
        <FormField
          :error="error(['opening', 'segments', index, 'channel'])"
          required
          :label="t('novelStudio.fields.channel')"
        >
          <Select
            :model-value="segment.channel"
            :options="channelOptions(segment.speaker_id)"
            :aria-label="t('novelStudio.fields.channel')"
            :invalid="invalid(['opening', 'segments', index, 'channel'])"
            :data-path="`opening.segments.${index}.channel`"
            block
            @update:model-value="write(['opening', 'segments', index, 'channel'], $event)"
          />
        </FormField>
        <FormField
          :error="error(['opening', 'segments', index, 'text'])"
          required
          class="is-wide"
          :label="t('novelStudio.fields.text')"
        >
          <Textarea
            v-bind="bind(['opening', 'segments', index, 'text'])"
            :rows="4"
            :data-testid="`novel-opening-${index}-text`"
          />
        </FormField>
      </div>
    </article>
  </section>
</template>
